/**
 * Fleet Explorer — Sprint 4.0 + Sprint 4.1
 *
 * Tabs:
 *   - Manage (4.0): customers/groups/tags + 5 cards
 *   - Map (4.1): mapa Leaflet + filtros laterais + cadastro de geo
 *
 * Não substitui o Dashboard NOC realtime; é página separada.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import MapView from "../components/fleet/MapView.jsx";
import {
  listCustomers,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  migrateFromCodes,
  listGroups,
  createGroup,
  updateGroup,
  deleteGroup,
  listTags,
  createTag,
  deleteTag,
  getDashboardSummary,
} from "../lib/api.js";
import { t } from "../i18n/index.js";
import Can from "../components/Can.jsx";
import { CAP } from "../lib/rbac.js";

function Card({ label, value, tone = "text", hint }) {
  const toneColor = {
    text: "text-noc-text",
    online: "text-noc-ok",
    offline: "text-noc-warn",
    error: "text-noc-err",
    muted: "text-noc-muted",
  }[tone] || "text-noc-text";
  return (
    <div className="bg-noc-panel border border-noc-border rounded p-3 flex flex-col gap-1">
      <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-noc-faint">
        {label}
      </div>
      <div className={`text-2xl font-mono ${toneColor}`}>{value}</div>
      {hint && (
        <div className="text-[10px] text-noc-muted/70">{hint}</div>
      )}
    </div>
  );
}

function SectionTitle({ children, action }) {
  return (
    <div className="flex items-center justify-between mt-6 mb-2 pb-1 border-b border-noc-border/40">
      <h2 className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-noc-muted">
        {children}
      </h2>
      {action}
    </div>
  );
}

function ErrorBanner({ error, onDismiss }) {
  if (!error) return null;
  return (
    <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-2 mb-2 flex items-start gap-2">
      <span className="flex-1 break-words">{error}</span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="text-noc-err hover:text-noc-err"
        >
          ×
        </button>
      )}
    </div>
  );
}

function SuccessBanner({ message, onDismiss }) {
  if (!message) return null;
  return (
    <div className="text-xs text-noc-ok bg-noc-ok/10 border border-noc-ok/30 rounded p-2 mb-2 flex items-start gap-2">
      <span className="flex-1 break-words">{message}</span>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="text-noc-ok hover:text-noc-ok"
        >
          ×
        </button>
      )}
    </div>
  );
}

export default function FleetExplorer({ onNavigate }) {
  const [summary, setSummary] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [filterCustomerId, setFilterCustomerId] = useState("");
  // Sprint 4.1: tabs "manage" (4.0) e "map" (4.1).
  // S3.11 — abre direto no mapa operacional; "Gerenciar" vira aba secundária.
  const [activeTab, setActiveTab] = useState("map");

  // forms
  const [newCustomer, setNewCustomer] = useState({
    code: "", name: "", contact: "", phone: "", email: "",
  });
  const [newGroup, setNewGroup] = useState({
    customer_id: "", name: "", description: "",
  });
  const [newTag, setNewTag] = useState({ name: "", color: "#888" });

  const refresh = useCallback(async (signal) => {
    try {
      setLoading(true);
      setError(null);
      const [s, c, g, t] = await Promise.all([
        getDashboardSummary({ signal }),
        listCustomers({ signal }),
        listGroups({
          customerId: filterCustomerId || null,
          signal,
        }),
        listTags({ signal }),
      ]);
      setSummary(s);
      setCustomers(c.customers || []);
      setGroups(g.groups || []);
      setTags(t.tags || []);
    } catch (e) {
      if (e?.name === "AbortError") return;
      setError(e?.message || "Erro ao carregar Fleet Explorer");
    } finally {
      setLoading(false);
    }
  }, [filterCustomerId]);

  useEffect(() => {
    const ctl = new AbortController();
    refresh(ctl.signal);
    return () => ctl.abort();
  }, [refresh]);

  // ---- Handlers customers ----
  const handleCreateCustomer = async () => {
    if (!newCustomer.code || !newCustomer.name) {
      setError("Cliente: code e name são obrigatórios.");
      return;
    }
    try {
      await createCustomer(newCustomer);
      setSuccess(`Cliente '${newCustomer.code}' criado.`);
      setNewCustomer({ code: "", name: "", contact: "", phone: "", email: "" });
      refresh();
    } catch (e) {
      setError(e?.message || "Erro ao criar cliente");
    }
  };

  const handleDeleteCustomer = async (c) => {
    if (!window.confirm(`Deletar cliente '${c.code}'? (Devices vinculados bloqueiam.)`)) return;
    try {
      await deleteCustomer(c.id);
      setSuccess(`Cliente '${c.code}' deletado.`);
      refresh();
    } catch (e) {
      setError(e?.message || `Não foi possível deletar '${c.code}'`);
    }
  };

  const handleMigrate = async (dryRun) => {
    try {
      const r = await migrateFromCodes({ dryRun });
      if (dryRun) {
        setSuccess(
          `Dry-run: criaria ${r.customers_created} cliente(s), `
          + `vincularia ${r.devices_linked} device(s). `
          + (r.created_codes.length > 0
            ? `Novos codes: ${r.created_codes.join(", ")}`
            : "Nada novo.")
        );
      } else {
        setSuccess(
          `Migração executada: ${r.customers_created} cliente(s) criado(s), `
          + `${r.devices_linked} device(s) vinculado(s).`
        );
        refresh();
      }
    } catch (e) {
      setError(e?.message || "Erro na migração");
    }
  };

  // ---- Handlers groups ----
  const handleCreateGroup = async () => {
    if (!newGroup.name) {
      setError("Grupo: name é obrigatório.");
      return;
    }
    try {
      await createGroup({
        customer_id: newGroup.customer_id || null,
        name: newGroup.name,
        description: newGroup.description || null,
      });
      setSuccess(`Grupo '${newGroup.name}' criado.`);
      setNewGroup({ customer_id: "", name: "", description: "" });
      refresh();
    } catch (e) {
      setError(e?.message || "Erro ao criar grupo");
    }
  };

  const handleDeleteGroup = async (g) => {
    if (!window.confirm(`Deletar grupo '${g.name}'? (Devices vinculados bloqueiam.)`)) return;
    try {
      await deleteGroup(g.id);
      setSuccess(`Grupo '${g.name}' deletado.`);
      refresh();
    } catch (e) {
      setError(e?.message || `Não foi possível deletar '${g.name}'`);
    }
  };

  // ---- Handlers tags ----
  const handleCreateTag = async () => {
    if (!newTag.name) {
      setError("Tag: name é obrigatório.");
      return;
    }
    try {
      await createTag(newTag);
      setSuccess(`Tag '${newTag.name}' criada.`);
      setNewTag({ name: "", color: "#888" });
      refresh();
    } catch (e) {
      setError(e?.message || "Erro ao criar tag");
    }
  };

  const handleDeleteTag = async (t) => {
    if (!window.confirm(`Deletar tag '${t.name}'? (Desvincula automaticamente dos devices.)`)) return;
    try {
      const r = await deleteTag(t.id);
      setSuccess(`Tag '${t.name}' deletada (${r.links_removed} vínculo(s) removido(s)).`);
      refresh();
    } catch (e) {
      setError(e?.message || `Não foi possível deletar '${t.name}'`);
    }
  };

  const customerByCode = useMemo(() => {
    const m = {};
    for (const c of customers) m[c.id] = c;
    return m;
  }, [customers]);

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Cabeçalho da página removido (título "Explorador da Frota" + subtítulo
          de sprints): redundante com o breadcrumb "NOC // MAPA DA FROTA" do topo
          e dava menos área ao mapa, que agora abre em tela cheia. O botão de
          refresh foi para a barra de abas (à direita). */}

      {/* Sprint 4.1: tabs */}
      <nav className="sticky top-[68px] z-20 flex items-center gap-1 rounded-xl border border-noc-border bg-noc-panel/95 p-1.5 backdrop-blur">
        <button
          type="button"
          onClick={() => setActiveTab("map")}
          className={`px-3.5 py-2 text-xs font-medium rounded-lg transition-colors ${
            activeTab === "map"
              ? "bg-noc-panel2 text-noc-accent shadow-sm"
              : "text-noc-muted hover:text-noc-text hover:bg-noc-panel2/50"
          }`}
        >
          Mapa operacional
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("manage")}
          className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px ${
            activeTab === "manage"
              ? "bg-noc-panel2 text-noc-accent shadow-sm"
              : "text-noc-muted hover:text-noc-text hover:bg-noc-panel2/50"
          }`}
        >
          Gerenciar
        </button>
        <span className="flex-1" />
        <button
          type="button"
          onClick={() => refresh()}
          disabled={loading}
          className="text-xs px-3 py-1 mb-1 rounded border bg-noc-panel2 text-noc-accent shadow-sm hover:border-noc-ok/30 disabled:opacity-50"
        >
          {loading ? t("common.loading") : `${t("common.refresh")}`}
        </button>
      </nav>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />
      <SuccessBanner message={success} onDismiss={() => setSuccess(null)} />

      {/* SPRINT 4.1 — Mapa */}
      {activeTab === "map" && <MapView onNavigate={onNavigate} />}

      {/* SPRINT 4.0 — Gestão (preserved) */}
      {activeTab === "manage" && <>
      {/* 5 CARDS NO TOPO */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card
          label="Clientes"
          value={customers.length}
          tone="text"
          hint="cadastrados"
        />
        <Card
          label="Grupos"
          value={groups.length}
          tone="text"
          hint={filterCustomerId ? "do cliente filtrado" : "total"}
        />
        <Card
          label="Devices"
          value={summary?.devices_total ?? "—"}
          tone="text"
        />
        <Card
          label="Online"
          value={summary?.devices_online ?? "—"}
          tone="online"
        />
        <Card
          label="Offline"
          value={summary?.devices_offline ?? "—"}
          tone="offline"
        />
      </div>

      {/* ===== CUSTOMERS ===== */}
      <SectionTitle
        action={
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleMigrate(true)}
              className="text-xs px-2 py-1 rounded border border-noc-warn/30 text-noc-warn hover:border-noc-warn/30"
              title="Simula migração sem alterar dados"
            >
              🔍 Dry-run migrate
            </button>
            <button
              type="button"
              onClick={() => handleMigrate(false)}
              className="text-xs px-2 py-1 rounded border bg-noc-panel2 text-noc-accent shadow-sm hover:border-noc-ok/30"
              title="Cria customers a partir de devices.customer_code legado"
            >
              ⚡ Migrate from codes
            </button>
          </div>
        }
      >
        Clientes ({customers.length})
      </SectionTitle>

      {/* form criar customer */}
      <div className="glass-card p-4 space-y-3">
        <div className="text-xs text-noc-muted">Novo cliente:</div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <input
            placeholder="code (ex: FIBRA-CANSANCAO)"
            value={newCustomer.code}
            onChange={(e) => setNewCustomer({ ...newCustomer, code: e.target.value })}
            className="bg-noc-bg border border-noc-border rounded px-2 py-1 text-xs"
          />
          <input
            placeholder="name"
            value={newCustomer.name}
            onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
            className="bg-noc-bg border border-noc-border rounded px-2 py-1 text-xs"
          />
          <input
            placeholder="contact"
            value={newCustomer.contact}
            onChange={(e) => setNewCustomer({ ...newCustomer, contact: e.target.value })}
            className="bg-noc-bg border border-noc-border rounded px-2 py-1 text-xs"
          />
          <input
            placeholder="phone"
            value={newCustomer.phone}
            onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
            className="bg-noc-bg border border-noc-border rounded px-2 py-1 text-xs"
          />
          <input
            placeholder="email"
            value={newCustomer.email}
            onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
            className="bg-noc-bg border border-noc-border rounded px-2 py-1 text-xs"
          />
        </div>
        <Can capability={CAP.FLEET_WRITE}>
        <button
          type="button"
          onClick={handleCreateCustomer}
          className="text-xs px-3 py-1 rounded bg-noc-ok hover:brightness-110 text-noc-bg font-semibold"
        >
          + Criar
        </button>
        </Can>
      </div>

      {/* lista de customers */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left uppercase tracking-wide text-noc-muted border-b border-noc-border/40">
              <th className="py-1 pr-2">Code</th>
              <th className="py-1 pr-2">Name</th>
              <th className="py-1 pr-2">Contact</th>
              <th className="py-1 pr-2">Phone</th>
              <th className="py-1 pr-2">Devices</th>
              <th className="py-1 pr-2"></th>
            </tr>
          </thead>
          <tbody>
            {customers.map((c) => (
              <tr key={c.id} className="border-b border-noc-border/20">
                <td className="py-1 pr-2 font-mono">{c.code}</td>
                <td className="py-1 pr-2">{c.name}</td>
                <td className="py-1 pr-2 text-noc-muted">{c.contact || "—"}</td>
                <td className="py-1 pr-2 text-noc-muted">{c.phone || "—"}</td>
                <td className="py-1 pr-2 font-mono">{c.devices_count}</td>
                <td className="py-1 pr-2">
                  <Can capability={CAP.FLEET_WRITE}>
                  <button
                    type="button"
                    onClick={() => handleDeleteCustomer(c)}
                    className="text-noc-err hover:text-noc-err text-[10px]"
                  >
                    deletar
                  </button>
                  </Can>
                </td>
              </tr>
            ))}
            {customers.length === 0 && !loading && (
              <tr><td colSpan="6" className="py-2 text-center text-noc-muted">
                Nenhum cliente cadastrado. Use "Migrate from codes" para criar a partir
                dos customer_code legados.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ===== GROUPS ===== */}
      <SectionTitle
        action={
          <select
            value={filterCustomerId}
            onChange={(e) => setFilterCustomerId(e.target.value)}
            className="bg-noc-bg border border-noc-border rounded px-2 py-0.5 text-xs"
          >
            <option value="">todos os clientes</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.code}</option>
            ))}
          </select>
        }
      >
        Grupos ({groups.length})
      </SectionTitle>

      <div className="bg-noc-panel border border-noc-border rounded p-3 space-y-2">
        <div className="text-xs text-noc-muted">Novo grupo:</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <select
            value={newGroup.customer_id}
            onChange={(e) => setNewGroup({ ...newGroup, customer_id: e.target.value })}
            className="bg-noc-bg border border-noc-border rounded px-2 py-1 text-xs"
          >
            <option value="">— sem cliente —</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.code}</option>
            ))}
          </select>
          <input
            placeholder="name"
            value={newGroup.name}
            onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
            className="bg-noc-bg border border-noc-border rounded px-2 py-1 text-xs"
          />
          <input
            placeholder="description (opcional)"
            value={newGroup.description}
            onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
            className="bg-noc-bg border border-noc-border rounded px-2 py-1 text-xs col-span-2"
          />
        </div>
        <Can capability={CAP.FLEET_WRITE}>
        <button
          type="button"
          onClick={handleCreateGroup}
          className="text-xs px-3 py-1 rounded bg-noc-ok hover:brightness-110 text-noc-bg font-semibold"
        >
          + Criar
        </button>
        </Can>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left uppercase tracking-wide text-noc-muted border-b border-noc-border/40">
              <th className="py-1 pr-2">Cliente</th>
              <th className="py-1 pr-2">Nome</th>
              <th className="py-1 pr-2">Descrição</th>
              <th className="py-1 pr-2">Devices</th>
              <th className="py-1 pr-2"></th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <tr key={g.id} className="border-b border-noc-border/20">
                <td className="py-1 pr-2 font-mono">
                  {g.customer_id ? (customerByCode[g.customer_id]?.code || g.customer_id.slice(0, 8)) : "—"}
                </td>
                <td className="py-1 pr-2">{g.name}</td>
                <td className="py-1 pr-2 text-noc-muted">{g.description || "—"}</td>
                <td className="py-1 pr-2 font-mono">{g.devices_count}</td>
                <td className="py-1 pr-2">
                  <Can capability={CAP.FLEET_WRITE}>
                  <button
                    type="button"
                    onClick={() => handleDeleteGroup(g)}
                    className="text-noc-err hover:text-noc-err text-[10px]"
                  >
                    deletar
                  </button>
                  </Can>
                </td>
              </tr>
            ))}
            {groups.length === 0 && !loading && (
              <tr><td colSpan="5" className="py-2 text-center text-noc-muted">
                Nenhum grupo cadastrado.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ===== TAGS ===== */}
      <SectionTitle>Tags ({tags.length})</SectionTitle>

      <div className="bg-noc-panel border border-noc-border rounded p-3 space-y-2">
        <div className="text-xs text-noc-muted">Nova tag:</div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            placeholder="name (ex: critical)"
            value={newTag.name}
            onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
            className="bg-noc-bg border border-noc-border rounded px-2 py-1 text-xs flex-1 min-w-[150px]"
          />
          <input
            type="color"
            value={newTag.color}
            onChange={(e) => setNewTag({ ...newTag, color: e.target.value })}
            className="h-7 w-12 bg-noc-bg border border-noc-border rounded cursor-pointer"
          />
          <Can capability={CAP.FLEET_WRITE}>
          <button
            type="button"
            onClick={handleCreateTag}
            className="text-xs px-3 py-1 rounded bg-noc-ok hover:brightness-110 text-noc-bg font-semibold"
          >
            + Criar
          </button>
          </Can>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {tags.map((t) => (
          <div
            key={t.id}
            className="inline-flex items-center gap-2 px-2 py-1 rounded border border-noc-border bg-noc-panel"
          >
            <span
              className="w-3 h-3 rounded-full inline-block"
              style={{ backgroundColor: t.color }}
            />
            <span className="text-xs font-mono">{t.name}</span>
            <Can capability={CAP.FLEET_WRITE}>
            <button
              type="button"
              onClick={() => handleDeleteTag(t)}
              className="text-noc-err hover:text-noc-err text-[10px] ml-1"
              title="Deletar tag (desvincula de todos os devices)"
            >
              ×
            </button>
            </Can>
          </div>
        ))}
        {tags.length === 0 && !loading && (
          <div className="text-xs text-noc-muted">Nenhuma tag cadastrada.</div>
        )}
      </div>
      </>}
    </div>
  );
}
